import { useCallback, useEffect, useRef, useState } from 'react';
import adminApiClient from '@/services/adminApiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Course {
  id: string; exam_id: string; slug: string; name: string;
  description: string | null; tagline: string | null; thumbnail_url: string | null;
  highlights: string[]; is_active: boolean; created_at: string;
  enrolled_count?: number;
}

interface CountryMeta { id: string; code: string; name: string; flag_emoji: string; }
interface ExamMeta { id: string; country_id: string; slug: string; display_name: string; }

interface ChapterPractice {
  practice_query: string;
  exam_context: string | null;
  question_count: number;
}

interface ChapterRow {
  id: string; slug: string; name: string; order_idx: number;
  is_high_weightage: boolean; is_active: boolean;
  topic_count: number;
  practice: ChapterPractice | null;
}

interface ModuleRow {
  id: string; name: string; order_idx: number; is_active: boolean;
  chapters: ChapterRow[];
}

interface SubjectRow {
  id: string; name: string; order_idx: number; is_active: boolean;
  modules: ModuleRow[];
}

interface PricingRow {
  id: string; course_id: string; country_code: string; currency: string;
  symbol: string; price: number; original_price: number | null; is_active: boolean;
}

interface DemoVideoGroup {
  exam_id: string; exam_name: string;
  videos: { id: string; tutor_video_url: string | null; practice_video_url: string | null; mock_exam_video_url: string | null; } | null;
}

interface Purchase {
  id: string; user_id: string;
  // Phone-authenticated accounts have no email, and email-authenticated ones
  // may have no phone — a buyer is identified by whichever exists.
  user_name: string | null; user_email: string | null; user_phone: string | null;
  amount: number; currency: string; status: string;
  purchased_at: string | null; created_at: string;
}

interface Topic {
  id: string; name: string; order_idx: number; learn_query: string | null;
}

interface CourseStats {
  enrolled_count: number;
  revenue: number;
}

interface CourseDetail extends Course {
  exam: { id: string; display_name: string; slug: string } | null;
  pricing: PricingRow[];
  structure: SubjectRow[];
  demo_videos: DemoVideoGroup[];
  stats: CourseStats;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

const api = {
  listCourses: (params?: Record<string, string>) =>
    adminApiClient.get('/courses', { params }).then(r => r.data as Course[]),
  listMeta: () =>
    adminApiClient.get('/courses/meta').then(r => r.data as { countries: CountryMeta[]; exams: ExamMeta[] }),
  getCourse: (id: string) =>
    adminApiClient.get(`/courses/${id}`).then(r => r.data as CourseDetail),
  updateCourse: (id: string, data: Partial<Course>) =>
    adminApiClient.patch(`/courses/${id}`, data).then(r => r.data),
  updateExam: (id: string, data: { display_name: string }) =>
    adminApiClient.patch(`/courses/exams/${id}`, data).then(r => r.data),
  upsertPricing: (courseId: string, data: object) =>
    adminApiClient.put(`/courses/${courseId}/pricing`, data).then(r => r.data),
  deletePricing: (courseId: string, cc: string) =>
    adminApiClient.delete(`/courses/${courseId}/pricing/${cc}`),
  upsertDemoVideos: (examId: string, data: object) =>
    adminApiClient.put(`/courses/exams/${examId}/demo-videos`, data).then(r => r.data),
  listPurchases: (courseId: string) =>
    adminApiClient.get(`/courses/${courseId}/purchases`).then(r => r.data as Purchase[]),
  grantAccess: (courseId: string, identifier: string) =>
    adminApiClient.post('/courses/grant-access', { identifier, course_id: courseId }).then(r => r.data),
  updateModule: (id: string, data: object) =>
    adminApiClient.patch(`/courses/modules/${id}`, data).then(r => r.data),
  updateChapter: (id: string, data: object) =>
    adminApiClient.patch(`/courses/chapters/${id}`, data).then(r => r.data),
  updateChapterPractice: (id: string, data: object) =>
    adminApiClient.put(`/courses/chapters/${id}/practice`, data).then(r => r.data),
  listTopics: (chapterId: string) =>
    adminApiClient.get(`/courses/chapters/${chapterId}/topics`).then(r => r.data as Topic[]),
  updateTopic: (id: string, data: object) =>
    adminApiClient.patch(`/courses/topics/${id}`, data).then(r => r.data),
};

// ─── Shared UI ────────────────────────────────────────────────────────────────

function Badge({ active, label }: { active: boolean; label?: string }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
      {label ?? (active ? 'Active' : 'Inactive')}
    </span>
  );
}

function InlineEdit({ value, onSave, className = '' }: {
  value: string; onSave: (v: string) => Promise<void>; className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  async function save() {
    if (draft === value) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(draft); setEditing(false); } finally { setSaving(false); }
  }

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <input
          ref={inputRef}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setDraft(value); } }}
          className={`border border-violet-400 rounded px-2 py-0.5 text-sm focus:outline-none ${className}`}
        />
        <button onClick={save} disabled={saving} className="text-xs text-violet-600 font-bold hover:text-violet-800">{saving ? '…' : '✓'}</button>
        <button onClick={() => { setEditing(false); setDraft(value); }} className="text-xs text-slate-400 hover:text-slate-600">✕</button>
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1.5 group ${className}`}>
      <span>{value}</span>
      <button
        onClick={() => setEditing(true)}
        className="opacity-30 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-violet-600"
        title="Rename"
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </button>
    </span>
  );
}

function EditableField({ label, value, multiline = false, horizontal = false, onSave }: {
  label: string; value: string; multiline?: boolean; horizontal?: boolean; onSave: (v: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

  async function save() {
    setSaving(true);
    try { await onSave(draft); setEditing(false); } finally { setSaving(false); }
  }

  if (horizontal) {
    return (
      <div className="flex items-start gap-4 py-3 border-b border-slate-100 last:border-0">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wide w-36 flex-shrink-0 pt-1">{label}</p>
        {editing ? (
          <div className="flex gap-2 items-start flex-1">
            {multiline ? (
              <textarea
                value={draft}
                onChange={e => setDraft(e.target.value)}
                rows={3}
                autoFocus
                className="flex-1 border border-violet-400 rounded-lg px-3 py-1.5 text-sm focus:outline-none resize-y"
              />
            ) : (
              <input
                value={draft}
                onChange={e => setDraft(e.target.value)}
                autoFocus
                className="flex-1 border border-violet-400 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
              />
            )}
            <button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs px-3 py-1.5 font-semibold transition-colors whitespace-nowrap">{saving ? '…' : 'Save'}</button>
            <button onClick={() => { setEditing(false); setDraft(value); }} className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs px-3 py-1.5 font-semibold transition-colors">Cancel</button>
          </div>
        ) : (
          <div className="flex gap-3 items-start flex-1">
            <span className={`text-sm flex-1 leading-relaxed ${value ? 'text-slate-800 font-medium' : 'text-slate-400 italic'}`}>{value || '(empty)'}</span>
            <button onClick={() => setEditing(true)} className="bg-violet-50 hover:bg-violet-100 text-violet-600 border border-violet-100 rounded text-xs px-2.5 py-1 font-semibold transition-colors whitespace-nowrap flex-shrink-0">Edit</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {label && <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}</p>}
      {editing ? (
        <div className="flex gap-2 items-start">
          {multiline ? (
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              rows={3}
              className="flex-1 border border-violet-400 rounded-lg px-3 py-1.5 text-sm focus:outline-none resize-y"
            />
          ) : (
            <input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              className="flex-1 border border-violet-400 rounded-lg px-3 py-1.5 text-sm focus:outline-none"
            />
          )}
          <button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs px-3 py-1.5 font-semibold transition-colors whitespace-nowrap">{saving ? '…' : 'Save'}</button>
          <button onClick={() => { setEditing(false); setDraft(value); }} className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs px-3 py-1.5 font-semibold transition-colors">Cancel</button>
        </div>
      ) : (
        <div className="flex gap-2 items-start">
          <span className={`text-sm flex-1 ${value ? 'text-slate-800 font-medium' : 'text-slate-400 italic'}`}>{value || '(empty)'}</span>
          <button onClick={() => setEditing(true)} className="bg-violet-50 hover:bg-violet-100 text-violet-600 border border-violet-100 rounded text-xs px-2 py-0.5 font-semibold transition-colors whitespace-nowrap">Edit</button>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Stats ───────────────────────────────────────────────────────────────

function StatsTab({ stats }: { stats: CourseStats }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-200 rounded-2xl px-8 py-8 flex flex-col items-center justify-center gap-2 text-center">
          <span className="text-4xl">👥</span>
          <p className="text-5xl font-black text-violet-800">{stats.enrolled_count.toLocaleString()}</p>
          <p className="text-sm font-semibold text-violet-500 uppercase tracking-widest">Students Enrolled</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-100 border border-green-200 rounded-2xl px-8 py-8 flex flex-col items-center justify-center gap-2 text-center">
          <span className="text-4xl">💰</span>
          <p className="text-5xl font-black text-green-800">₹{stats.revenue.toLocaleString()}</p>
          <p className="text-sm font-semibold text-green-500 uppercase tracking-widest">Total Revenue</p>
        </div>
      </div>
      <div className="bg-slate-50 border border-slate-100 rounded-xl px-5 py-4">
        <p className="text-xs text-slate-400">Revenue includes only completed purchases. To see individual records, go to the Purchases tab.</p>
      </div>
    </div>
  );
}

// ─── Tab: Info ────────────────────────────────────────────────────────────────

function HighlightsEditor({ highlights, onSave }: { highlights: string[]; onSave: (v: string[]) => Promise<void> }) {
  const [items, setItems] = useState<string[]>(highlights);
  const [newItem, setNewItem] = useState('');
  const [saving, setSaving] = useState(false);

  async function save(updated: string[]) {
    setSaving(true);
    try { await onSave(updated); setItems(updated); } finally { setSaving(false); }
  }

  function add() {
    if (!newItem.trim()) return;
    save([...items, newItem.trim()]);
    setNewItem('');
  }

  function remove(i: number) {
    save(items.filter((_, idx) => idx !== i));
  }

  return (
    <div>
      <div className="space-y-1.5 mb-3">
        {items.map((h, i) => (
          <div key={i} className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
            <span className="text-emerald-600 font-bold text-sm">✓</span>
            <span className="flex-1 text-sm text-slate-700 font-medium">{h}</span>
            <button onClick={() => remove(i)} disabled={saving} className="text-red-400 hover:text-red-600 text-xs font-bold transition-colors w-5 h-5 rounded-full hover:bg-red-50 flex items-center justify-center">✕</button>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-slate-400 italic">No highlights yet</p>}
      </div>
      <div className="flex gap-2">
        <input
          value={newItem}
          onChange={e => setNewItem(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder="Add a highlight…"
          className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-violet-400"
        />
        <button onClick={add} disabled={saving || !newItem.trim()} className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg text-sm px-3 py-1.5 font-semibold transition-colors">
          {saving ? '…' : 'Add'}
        </button>
      </div>
    </div>
  );
}

function InfoTab({ course, onUpdate, onUpdateExam }: {
  course: CourseDetail;
  onUpdate: (data: Partial<Course>) => Promise<void>;
  onUpdateExam: (data: { display_name: string }) => Promise<void>;
}) {
  return (
    <div className="space-y-5">

      {/* ── Course Details ────────────────── */}
      <div className="rounded-xl overflow-hidden border border-indigo-100 shadow-sm">
        <div className="bg-gradient-to-r from-indigo-500 to-violet-600 px-5 py-3 flex items-center gap-2">
          <span className="text-white text-base">📋</span>
          <p className="text-sm font-bold text-white tracking-wide">Course Details</p>
        </div>
        <div className="bg-white px-5">
          <EditableField horizontal label="Course Name" value={course.name} onSave={v => onUpdate({ name: v })} />
          <EditableField horizontal label="Tagline" value={course.tagline ?? ''} onSave={v => onUpdate({ tagline: v })} />
          <EditableField horizontal label="Description" value={course.description ?? ''} multiline onSave={v => onUpdate({ description: v })} />
          <EditableField horizontal label="Thumbnail URL" value={course.thumbnail_url ?? ''} onSave={v => onUpdate({ thumbnail_url: v })} />
          <div className="flex items-center gap-4 py-3 pb-4">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wide w-36 flex-shrink-0">Slug</p>
            <p className="text-sm text-slate-400 font-mono bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-1.5 flex-1 truncate">{course.slug}</p>
          </div>
          {course.thumbnail_url && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Thumbnail Preview</p>
              <img
                src={course.thumbnail_url}
                alt="thumbnail"
                className="h-24 w-36 object-cover rounded-xl border border-indigo-100 shadow-sm"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Status & Exam ─────────────────── */}
      <div className="rounded-xl overflow-hidden border border-teal-100 shadow-sm">
        <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-5 py-3 flex items-center gap-2">
          <span className="text-white text-base">⚙️</span>
          <p className="text-sm font-bold text-white tracking-wide">Status &amp; Exam</p>
        </div>
        <div className="p-5 space-y-4 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Course Status</p>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${course.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                  <span className={`w-2 h-2 rounded-full ${course.is_active ? 'bg-green-500' : 'bg-red-400'}`} />
                  {course.is_active ? 'Active' : 'Inactive'}
                </span>
                <button
                  onClick={() => onUpdate({ is_active: !course.is_active })}
                  className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors border ${
                    course.is_active
                      ? 'bg-red-50 hover:bg-red-100 text-red-600 border-red-200'
                      : 'bg-green-50 hover:bg-green-100 text-green-600 border-green-200'
                  }`}
                >
                  {course.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
            {course.exam && (
              <div className="bg-teal-50 border border-teal-100 rounded-xl px-4 py-3">
                <EditableField
                  label="Exam Display Name"
                  value={course.exam.display_name}
                  onSave={v => onUpdateExam({ display_name: v })}
                />
                <p className="text-xs text-teal-400 font-mono mt-1">/{course.exam.slug}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Highlights ────────────────────── */}
      <div className="rounded-xl overflow-hidden border border-emerald-100 shadow-sm">
        <div className="bg-gradient-to-r from-emerald-500 to-green-500 px-5 py-3 flex items-center gap-2">
          <span className="text-white text-base">✨</span>
          <p className="text-sm font-bold text-white tracking-wide">Course Highlights</p>
        </div>
        <div className="p-5 bg-white">
          <HighlightsEditor
            highlights={course.highlights ?? []}
            onSave={v => onUpdate({ highlights: v })}
          />
        </div>
      </div>

    </div>
  );
}

// ─── Tab: Pricing ─────────────────────────────────────────────────────────────

const COUNTRY_CURRENCY: Record<string, { currency: string; symbol: string; label: string }> = {
  IN: { currency: 'INR', symbol: '₹',  label: '🇮🇳 India' },
  US: { currency: 'USD', symbol: '$',  label: '🇺🇸 United States' },
  GB: { currency: 'GBP', symbol: '£',  label: '🇬🇧 United Kingdom' },
  AE: { currency: 'AED', symbol: 'د.إ', label: '🇦🇪 UAE' },
  AU: { currency: 'AUD', symbol: 'A$', label: '🇦🇺 Australia' },
  CA: { currency: 'CAD', symbol: 'C$', label: '🇨🇦 Canada' },
  SG: { currency: 'SGD', symbol: 'S$', label: '🇸🇬 Singapore' },
  NZ: { currency: 'NZD', symbol: 'NZ$', label: '🇳🇿 New Zealand' },
  ZA: { currency: 'ZAR', symbol: 'R',  label: '🇿🇦 South Africa' },
  EU: { currency: 'EUR', symbol: '€',  label: '🇪🇺 Europe' },
  MY: { currency: 'MYR', symbol: 'RM', label: '🇲🇾 Malaysia' },
  NG: { currency: 'NGN', symbol: '₦',  label: '🇳🇬 Nigeria' },
  BD: { currency: 'BDT', symbol: '৳',  label: '🇧🇩 Bangladesh' },
  NP: { currency: 'NPR', symbol: 'रू', label: '🇳🇵 Nepal' },
  LK: { currency: 'LKR', symbol: 'Rs', label: '🇱🇰 Sri Lanka' },
};

function PricingTab({ courseId, pricing, onRefresh }: { courseId: string; pricing: PricingRow[]; onRefresh: () => void }) {
  const [form, setForm] = useState({ country_code: '', currency: '', symbol: '', price: '', original_price: '' });
  const [editingCountry, setEditingCountry] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const formRef = useRef<HTMLDivElement>(null);

  function handleCountryChange(cc: string) {
    const meta = COUNTRY_CURRENCY[cc];
    setForm(prev => ({
      ...prev,
      country_code: cc,
      currency: meta?.currency ?? '',
      symbol: meta?.symbol ?? '',
    }));
  }

  function startEdit(row: PricingRow) {
    setEditingCountry(row.country_code);
    setForm({
      country_code: row.country_code,
      currency: row.currency,
      symbol: row.symbol,
      price: String(row.price),
      original_price: row.original_price != null ? String(row.original_price) : '',
    });
    setError('');
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function cancelEdit() {
    setEditingCountry(null);
    setForm({ country_code: '', currency: '', symbol: '', price: '', original_price: '' });
    setError('');
  }

  async function upsert() {
    if (!form.country_code || !form.price) { setError('Country and price are required'); return; }
    setSaving(true); setError('');
    try {
      await api.upsertPricing(courseId, {
        country_code: form.country_code,
        currency: form.currency,
        symbol: form.symbol,
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        is_active: true,
      });
      setEditingCountry(null);
      setForm({ country_code: '', currency: '', symbol: '', price: '', original_price: '' });
      onRefresh();
    } catch (e: any) { setError(e.response?.data?.detail ?? e.message); }
    finally { setSaving(false); }
  }

  async function remove(cc: string) {
    if (!confirm(`Remove pricing for ${cc}?`)) return;
    if (editingCountry === cc) cancelEdit();
    await api.deletePricing(courseId, cc);
    onRefresh();
  }

  const selectedMeta = COUNTRY_CURRENCY[form.country_code];
  const isEditing = editingCountry !== null;

  return (
    <div>
      <table className="w-full text-sm mb-6 border-collapse">
        <thead>
          <tr className="bg-slate-50 text-left">
            {['Country', 'Currency', 'Price', 'Original', 'Active', ''].map(h => (
              <th key={h} className="px-3 py-2 font-semibold text-slate-600 text-xs uppercase">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pricing.map(p => (
            <tr
              key={p.id}
              className={`border-b border-slate-100 transition-colors ${editingCountry === p.country_code ? 'bg-violet-50' : ''}`}
            >
              <td className="px-3 py-2 font-bold">
                {COUNTRY_CURRENCY[p.country_code]?.label ?? p.country_code}
                {editingCountry === p.country_code && (
                  <span className="ml-2 text-xs bg-violet-200 text-violet-700 px-1.5 py-0.5 rounded-full font-semibold">Editing</span>
                )}
              </td>
              <td className="px-3 py-2">
                <span className="font-semibold text-slate-600">{p.currency}</span>
                <span className="ml-1.5 text-slate-400">{p.symbol}</span>
              </td>
              <td className="px-3 py-2 font-bold text-violet-700">{p.symbol}{p.price.toLocaleString()}</td>
              <td className="px-3 py-2 text-slate-400">
                {p.original_price ? (
                  <span className="line-through text-slate-400">{p.symbol}{p.original_price.toLocaleString()}</span>
                ) : '—'}
              </td>
              <td className="px-3 py-2"><Badge active={p.is_active} /></td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => startEdit(p)}
                    className="text-violet-600 hover:text-violet-800 text-xs font-semibold"
                  >Edit</button>
                  <button
                    onClick={() => remove(p.country_code)}
                    className="text-red-500 hover:text-red-700 text-xs font-semibold"
                  >Remove</button>
                </div>
              </td>
            </tr>
          ))}
          {pricing.length === 0 && (
            <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400 text-sm">No pricing configured</td></tr>
          )}
        </tbody>
      </table>

      {/* Add / Edit form */}
      <div
        ref={formRef}
        className={`rounded-xl p-5 border transition-colors ${isEditing ? 'bg-violet-50 border-violet-200' : 'bg-slate-50 border-slate-200'}`}
      >
        {/* Form header */}
        <div className="flex items-center justify-between mb-4">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
              <p className="text-sm font-bold text-violet-800">
                Editing — {COUNTRY_CURRENCY[editingCountry!]?.label ?? editingCountry}
              </p>
            </div>
          ) : (
            <p className="text-sm font-bold text-slate-700">Add New Pricing</p>
          )}
          {isEditing && (
            <button
              onClick={cancelEdit}
              className="text-xs text-slate-500 hover:text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-1 font-semibold transition-colors"
            >
              Cancel Edit
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">

          {/* Country — select when adding, locked display when editing */}
          <div>
            <p className="text-xs text-slate-500 font-semibold mb-1.5 uppercase tracking-wide">Country</p>
            {isEditing ? (
              <div className="flex items-center gap-2 bg-white border border-violet-200 rounded-lg px-3 py-2">
                <span className="text-sm font-bold text-violet-800">{COUNTRY_CURRENCY[editingCountry!]?.label ?? editingCountry}</span>
                <span className="ml-auto text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded font-mono">{editingCountry}</span>
              </div>
            ) : (
              <select
                value={form.country_code}
                onChange={e => handleCountryChange(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-400 bg-white"
              >
                <option value="">— Select country —</option>
                {Object.entries(COUNTRY_CURRENCY).map(([code, meta]) => (
                  <option key={code} value={code}>{meta.label} ({code})</option>
                ))}
              </select>
            )}
          </div>

          {/* Currency info tile — auto-filled */}
          {selectedMeta && (
            <div className="bg-white border border-violet-100 rounded-xl px-4 py-2.5 flex items-center gap-4">
              <div className="text-center">
                <p className="text-xs text-violet-400 font-semibold uppercase tracking-wide mb-0.5">Currency</p>
                <p className="text-base font-extrabold text-violet-700">{selectedMeta.currency}</p>
              </div>
              <div className="w-px h-8 bg-violet-100" />
              <div className="text-center">
                <p className="text-xs text-violet-400 font-semibold uppercase tracking-wide mb-0.5">Symbol</p>
                <p className="text-xl font-extrabold text-violet-700">{selectedMeta.symbol}</p>
              </div>
              <p className="text-xs text-violet-300 ml-auto italic">auto-filled</p>
            </div>
          )}

          {/* Price */}
          <div>
            <p className="text-xs text-slate-500 font-semibold mb-1.5 uppercase tracking-wide">Price</p>
            <div className="relative">
              {selectedMeta && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400 pointer-events-none">
                  {selectedMeta.symbol}
                </span>
              )}
              <input
                value={form.price}
                onChange={e => setForm(p => ({ ...p, price: e.target.value }))}
                placeholder="4999"
                type="number"
                min="0"
                className={`w-full border rounded-lg py-2 text-sm focus:outline-none ${isEditing ? 'border-violet-300 focus:border-violet-500 bg-white' : 'border-slate-200 focus:border-violet-400'} ${selectedMeta ? 'pl-8 pr-3' : 'px-3'}`}
              />
            </div>
          </div>

          {/* Original Price */}
          <div>
            <p className="text-xs text-slate-500 font-semibold mb-1.5 uppercase tracking-wide">
              Original Price <span className="text-slate-400 normal-case font-normal">(optional, for strike-through)</span>
            </p>
            <div className="relative">
              {selectedMeta && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-400 pointer-events-none">
                  {selectedMeta.symbol}
                </span>
              )}
              <input
                value={form.original_price}
                onChange={e => setForm(p => ({ ...p, original_price: e.target.value }))}
                placeholder="9999"
                type="number"
                min="0"
                className={`w-full border rounded-lg py-2 text-sm focus:outline-none ${isEditing ? 'border-violet-300 focus:border-violet-500 bg-white' : 'border-slate-200 focus:border-violet-400'} ${selectedMeta ? 'pl-8 pr-3' : 'px-3'}`}
              />
            </div>
          </div>

        </div>

        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        <div className="flex items-center gap-3">
          <button
            onClick={upsert}
            disabled={saving || !form.country_code || !form.price}
            className={`font-semibold text-sm px-5 py-2 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isEditing ? 'bg-violet-600 hover:bg-violet-700' : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            {saving ? 'Saving…' : isEditing ? `Update ${COUNTRY_CURRENCY[editingCountry!]?.label ?? editingCountry} Pricing` : 'Add Pricing'}
          </button>
          {isEditing && (
            <button onClick={cancelEdit} className="text-sm text-slate-500 hover:text-slate-700 font-semibold transition-colors">
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Demo Videos ─────────────────────────────────────────────────────────

function VideosTab({ groups, onSave }: {
  groups: DemoVideoGroup[];
  onSave: (examId: string, data: object) => Promise<void>;
}) {
  return (
    <div className="space-y-4">
      {groups.map(g => <VideoGroupCard key={g.exam_id} group={g} onSave={data => onSave(g.exam_id, data)} />)}
      {groups.length === 0 && <p className="text-slate-400 text-sm">No exams found for this course.</p>}
    </div>
  );
}

function VideoGroupCard({ group, onSave }: { group: DemoVideoGroup; onSave: (data: object) => Promise<void> }) {
  const [form, setForm] = useState({
    tutor_video_url: group.videos?.tutor_video_url ?? '',
    practice_video_url: group.videos?.practice_video_url ?? '',
    mock_exam_video_url: group.videos?.mock_exam_video_url ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave({
        tutor_video_url: form.tutor_video_url || null,
        practice_video_url: form.practice_video_url || null,
        mock_exam_video_url: form.mock_exam_video_url || null,
      });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white border border-admin-border rounded-xl p-4">
      <p className="font-bold text-sm text-slate-800 mb-3">{group.exam_name}</p>
      {[
        { label: '🎓 Tutor Video URL', key: 'tutor_video_url' },
        { label: '📝 Practice Video URL', key: 'practice_video_url' },
        { label: '📋 Mock Exam Video URL', key: 'mock_exam_video_url' },
      ].map(f => (
        <div key={f.key} className="mb-3">
          <p className="text-xs text-slate-500 font-medium mb-1">{f.label}</p>
          <input
            value={(form as any)[f.key]}
            onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
            placeholder="https://..."
            className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-violet-400"
          />
        </div>
      ))}
      <button
        onClick={handleSave}
        disabled={saving}
        className={`text-sm px-4 py-2 rounded-lg font-semibold text-white transition-colors ${saved ? 'bg-green-600' : 'bg-violet-600 hover:bg-violet-700'}`}
      >
        {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Videos'}
      </button>
    </div>
  );
}

// ─── Tab: Structure ───────────────────────────────────────────────────────────

function PracticeEditor({ chapterId, practice, onRefresh }: {
  chapterId: string;
  practice: ChapterPractice | null;
  onRefresh: () => void;
}) {
  const [form, setForm] = useState({
    practice_query: practice?.practice_query ?? '',
    exam_context: practice?.exam_context ?? '',
    question_count: practice?.question_count ?? 8,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await api.updateChapterPractice(chapterId, {
        practice_query: form.practice_query,
        exam_context: form.exam_context || null,
        question_count: Number(form.question_count),
      });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      onRefresh();
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 mt-2">
      <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2">Practice Config</p>
      <div className="space-y-2">
        <div>
          <p className="text-xs text-slate-500 font-medium mb-1">Practice Query</p>
          <textarea
            value={form.practice_query}
            onChange={e => setForm(p => ({ ...p, practice_query: e.target.value }))}
            rows={2}
            placeholder="e.g. Questions on Newton's Laws of Motion for JEE…"
            className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-amber-400 resize-none bg-white"
          />
        </div>
        <div>
          <p className="text-xs text-slate-500 font-medium mb-1">Exam Context (optional)</p>
          <input
            value={form.exam_context}
            onChange={e => setForm(p => ({ ...p, exam_context: e.target.value }))}
            placeholder="JEE Mains / Advanced…"
            className="w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-amber-400 bg-white"
          />
        </div>
        <div className="flex items-center gap-3">
          <div>
            <p className="text-xs text-slate-500 font-medium mb-1">Question Count</p>
            <input
              type="number"
              value={form.question_count}
              onChange={e => setForm(p => ({ ...p, question_count: parseInt(e.target.value) || 8 }))}
              min={1} max={30}
              className="w-20 border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-amber-400 bg-white"
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`mt-4 text-sm px-3 py-1.5 rounded-lg font-semibold text-white transition-colors ${saved ? 'bg-green-600' : 'bg-amber-600 hover:bg-amber-700'}`}
          >
            {saving ? '…' : saved ? '✓ Saved' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TopicItem({ topic, onSave }: { topic: Topic; onSave: (data: object) => Promise<void> }) {
  const [editingName, setEditingName] = useState(false);
  const [editingQuery, setEditingQuery] = useState(false);
  const [nameDraft, setNameDraft] = useState(topic.name);
  const [queryDraft, setQueryDraft] = useState(topic.learn_query ?? '');
  const [saving, setSaving] = useState(false);

  async function save(data: object) {
    setSaving(true);
    try { await onSave(data); } finally { setSaving(false); }
  }

  return (
    <div className="border border-violet-100 rounded-lg px-3 py-2 bg-violet-50/30 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400 w-5 text-center font-mono">{topic.order_idx}</span>
        {editingName ? (
          <span className="flex-1 flex items-center gap-1.5">
            <input
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { save({ name: nameDraft }); setEditingName(false); }
                if (e.key === 'Escape') { setEditingName(false); setNameDraft(topic.name); }
              }}
              className="flex-1 border border-violet-400 rounded px-2 py-0.5 text-sm focus:outline-none"
              autoFocus
            />
            <button onClick={() => { save({ name: nameDraft }); setEditingName(false); }} disabled={saving} className="text-xs text-violet-600 font-bold">{saving ? '…' : '✓'}</button>
            <button onClick={() => { setEditingName(false); setNameDraft(topic.name); }} className="text-xs text-slate-400">✕</button>
          </span>
        ) : (
          <span className="flex-1 flex items-center gap-1.5 group">
            <span className="text-sm font-medium text-slate-700">{topic.name}</span>
            <button onClick={() => setEditingName(true)} className="opacity-30 group-hover:opacity-100 transition-opacity text-slate-300 hover:text-violet-600">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
          </span>
        )}
        <button onClick={() => setEditingQuery(!editingQuery)} className="text-xs text-indigo-500 hover:text-indigo-700 font-medium whitespace-nowrap">
          {editingQuery ? 'Hide Query' : 'Edit Query'}
        </button>
      </div>
      {editingQuery && (
        <div className="ml-7">
          <input
            value={queryDraft}
            onChange={e => setQueryDraft(e.target.value)}
            placeholder="Learn query for AI tutor…"
            className="w-full border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-violet-400"
          />
          <div className="flex gap-2 mt-1">
            <button onClick={() => { save({ learn_query: queryDraft }); setEditingQuery(false); }} disabled={saving} className="text-xs bg-indigo-600 text-white rounded px-2 py-0.5 font-semibold">
              {saving ? '…' : 'Save'}
            </button>
            <button onClick={() => { setEditingQuery(false); setQueryDraft(topic.learn_query ?? ''); }} className="text-xs text-slate-400">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChapterRowItem({ chapter, onRefresh }: { chapter: ChapterRow; onRefresh: () => void }) {
  const [showPractice, setShowPractice] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const [topics, setTopics] = useState<Topic[] | null>(null);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  async function loadTopics() {
    if (topics !== null) return;
    setTopicsLoading(true);
    try { setTopics(await api.listTopics(chapter.id)); } finally { setTopicsLoading(false); }
  }

  function handleToggleTopics() {
    if (!showTopics) loadTopics();
    setShowTopics(v => !v);
  }

  async function update(data: object) {
    setSaving(true);
    try { await api.updateChapter(chapter.id, data); onRefresh(); } finally { setSaving(false); }
  }

  async function updateTopic(topicId: string, data: object) {
    await api.updateTopic(topicId, data);
    setTopics(await api.listTopics(chapter.id));
  }

  return (
    <div className="ml-8 mb-1">
      <div className="flex items-center gap-2 px-3 py-1.5 mx-4 my-0.5 rounded-lg hover:bg-violet-50/60 transition-colors border-l-2 border-violet-100">
        {/* Active ON/OFF pill */}
        <button
          onClick={() => update({ is_active: !chapter.is_active })}
          disabled={saving}
          className={`text-xs px-2 py-0.5 rounded-full font-semibold border transition-colors whitespace-nowrap ${
            chapter.is_active
              ? 'bg-green-50 text-green-600 border-green-200 hover:bg-red-50 hover:text-red-500 hover:border-red-200'
              : 'bg-red-50 text-red-500 border-red-200 hover:bg-green-50 hover:text-green-600 hover:border-green-200'
          }`}
          title={chapter.is_active ? 'Click to deactivate' : 'Click to activate'}
        >
          {chapter.is_active ? 'ON' : 'OFF'}
        </button>

        {/* HW badge toggle */}
        <button
          onClick={() => update({ is_high_weightage: !chapter.is_high_weightage })}
          disabled={saving}
          title={chapter.is_high_weightage ? 'Remove High Weightage' : 'Mark High Weightage'}
          className={`text-xs font-bold px-1.5 py-0.5 rounded-full border transition-colors ${
            chapter.is_high_weightage
              ? 'bg-amber-100 text-amber-700 border-amber-300'
              : 'text-slate-300 border-slate-200 hover:border-amber-300 hover:text-amber-400'
          }`}
        >HW</button>

        {/* Name (inline edit) */}
        <InlineEdit
          value={chapter.name}
          onSave={v => update({ name: v })}
          className="flex-1 text-sm text-violet-900 font-medium"
        />

        {/* Action buttons — always visible */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowPractice(v => !v)}
            className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${
              showPractice ? 'bg-amber-100 text-amber-700' : 'text-slate-400 hover:text-amber-600 hover:bg-amber-50'
            }`}
          >⚙ Practice</button>
          <button
            onClick={handleToggleTopics}
            className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${
              showTopics ? 'bg-indigo-100 text-indigo-700' : 'text-slate-400 hover:text-indigo-600 hover:bg-indigo-50'
            }`}
          >Topics{chapter.topic_count > 0 ? ` (${chapter.topic_count})` : ''}</button>
        </div>
      </div>

      {showPractice && (
        <div className="ml-8 mr-2">
          <PracticeEditor chapterId={chapter.id} practice={chapter.practice} onRefresh={onRefresh} />
        </div>
      )}

      {showTopics && (
        <div className="ml-8 mr-2 mt-1 mb-2">
          {topicsLoading ? (
            <p className="text-xs text-slate-400 py-2">Loading topics…</p>
          ) : topics && topics.length > 0 ? (
            <div className="space-y-1">
              {topics.map(t => (
                <TopicItem key={t.id} topic={t} onSave={data => updateTopic(t.id, data)} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic py-1">No topics found</p>
          )}
        </div>
      )}
    </div>
  );
}

function ModuleRowItem({ module: mod, onRefresh }: { module: ModuleRow; onRefresh: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  async function update(data: object) {
    setSaving(true);
    try { await api.updateModule(mod.id, data); onRefresh(); } finally { setSaving(false); }
  }

  return (
    <div className="ml-4 mt-1">
      <div className="flex items-center gap-2 px-3 py-2 mx-2 my-0.5 rounded-lg hover:bg-indigo-50 transition-colors border-l-4 border-indigo-200">
        {/* Expand toggle */}
        <button onClick={() => setExpanded(v => !v)} className="text-indigo-400 hover:text-indigo-700 w-4 text-center transition-colors flex-shrink-0 font-bold">
          {expanded ? '▾' : '▸'}
        </button>

        {/* Active ON/OFF pill */}
        <button
          onClick={() => update({ is_active: !mod.is_active })}
          disabled={saving}
          className={`text-xs px-2 py-0.5 rounded-full font-semibold border transition-colors whitespace-nowrap flex-shrink-0 ${
            mod.is_active
              ? 'bg-green-50 text-green-600 border-green-200 hover:bg-red-50 hover:text-red-500 hover:border-red-200'
              : 'bg-red-50 text-red-500 border-red-200 hover:bg-green-50 hover:text-green-600 hover:border-green-200'
          }`}
          title={mod.is_active ? 'Click to deactivate' : 'Click to activate'}
        >
          {mod.is_active ? 'ON' : 'OFF'}
        </button>

        {/* Name (inline edit) */}
        <InlineEdit
          value={mod.name}
          onSave={v => update({ name: v })}
          className="flex-1 text-sm font-semibold text-indigo-800"
        />

        <span className="text-xs text-indigo-300 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full font-medium flex-shrink-0">{mod.chapters.length} ch</span>
      </div>

      {expanded && (
        <div>
          {mod.chapters.map(ch => (
            <ChapterRowItem key={ch.id} chapter={ch} onRefresh={onRefresh} />
          ))}
          {mod.chapters.length === 0 && (
            <p className="ml-8 text-xs text-slate-400 italic py-1">No chapters</p>
          )}
        </div>
      )}
    </div>
  );
}

function StructureTab({ structure, onRefresh }: { structure: SubjectRow[]; onRefresh: () => void }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-2.5 font-medium">
        <span>💡</span>
        <span>Click ▸ to expand modules · Hover chapter names to rename · Click <strong>ON/OFF</strong> to toggle active · Click <strong>HW</strong> to toggle high-weightage</span>
      </div>
      {structure.map(subj => (
        <div key={subj.id} className="rounded-xl overflow-hidden border border-indigo-200 shadow-sm">
          {/* Subject header — deep violet gradient */}
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-3 flex items-center gap-3">
            <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${subj.is_active ? 'bg-green-300' : 'bg-red-300'}`} />
            <span className="text-sm font-extrabold text-white tracking-wide flex-1">{subj.name}</span>
            <span className="text-xs text-indigo-200 bg-indigo-700/40 px-2.5 py-0.5 rounded-full">{subj.modules.length} modules</span>
          </div>
          {/* Modules */}
          <div className="py-2 bg-white">
            {subj.modules.map(mod => (
              <ModuleRowItem key={mod.id} module={mod} onRefresh={onRefresh} />
            ))}
          </div>
        </div>
      ))}
      {structure.length === 0 && <p className="text-slate-400 text-sm">No structure found.</p>}
    </div>
  );
}

// ─── Tab: Purchases ───────────────────────────────────────────────────────────

function PurchasesTab({ courseId }: { courseId: string }) {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [grantIdentifier, setGrantIdentifier] = useState('');
  const [granting, setGranting] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    api.listPurchases(courseId).then(data => { setPurchases(data); setLoading(false); });
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  async function handleGrant() {
    if (!grantIdentifier) return;
    setGranting(true); setMsg('');
    try {
      const res = await api.grantAccess(courseId, grantIdentifier);
      setMsg(res.message); setGrantIdentifier(''); load();
    } catch (e: any) { setMsg(e.response?.data?.detail ?? e.message); }
    finally { setGranting(false); }
  }

  return (
    <div>
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-5 flex flex-wrap gap-3 items-center">
        <input
          value={grantIdentifier}
          onChange={e => setGrantIdentifier(e.target.value)}
          placeholder="Email, phone number, or user id"
          className="flex-1 min-w-40 border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-violet-400"
          onKeyDown={e => e.key === 'Enter' && handleGrant()}
        />
        <button onClick={handleGrant} disabled={granting} className="bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-semibold text-sm px-3 py-1.5 transition-colors">
          {granting ? '…' : 'Grant Free Access'}
        </button>
        {msg && <span className="text-sm text-green-600 font-medium">{msg}</span>}
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Loading…</p>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50 text-left">
              {['User', 'Email / Phone', 'Amount', 'Status', 'Date'].map(h => (
                <th key={h} className="px-3 py-2 font-semibold text-slate-600 text-xs uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {purchases.map(p => (
              <tr key={p.id} className="border-b border-slate-100">
                <td className="px-3 py-2 font-semibold text-slate-800">{p.user_name ?? '—'}</td>
                <td className="px-3 py-2 text-slate-500">{p.user_email ?? p.user_phone ?? '—'}</td>
                <td className="px-3 py-2">{p.currency} {p.amount}</td>
                <td className="px-3 py-2"><Badge active={p.status === 'completed'} /></td>
                <td className="px-3 py-2 text-slate-400 text-xs">
                  {p.purchased_at ? new Date(p.purchased_at).toLocaleDateString('en-GB') : '—'}
                </td>
              </tr>
            ))}
            {purchases.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-5 text-center text-slate-400">No purchases yet</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Course Detail Panel ──────────────────────────────────────────────────────

const TABS = [
  { key: 'info', label: 'Info' },
  { key: 'stats', label: 'Stats' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'videos', label: 'Demo Videos' },
  { key: 'structure', label: 'Structure' },
  { key: 'purchases', label: 'Purchases' },
] as const;

type TabKey = typeof TABS[number]['key'];

function CourseDetailPanel({ courseId, onBack }: { courseId: string; onBack: () => void }) {
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(async () => {
    setLoading(true);
    try { setCourse(await api.getCourse(courseId)); } finally { setLoading(false); }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  async function handleUpdate(data: Partial<Course>) {
    await api.updateCourse(courseId, data);
    await load();
  }

  async function handleUpdateExam(data: { display_name: string }) {
    if (!course?.exam) return;
    await api.updateExam(course.exam.id, data);
    await load();
  }

  async function handleSaveVideos(examId: string, data: object) {
    await api.upsertDemoVideos(examId, data);
    await load();
  }

  if (loading) return <div className="py-20 text-center text-slate-400">Loading…</div>;
  if (!course) return null;

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 text-sm mb-5 transition-colors">
        ← Back to courses
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-extrabold text-slate-800">{course.name}</h1>
          <Badge active={course.is_active} />
        </div>
        <span className="text-xs text-slate-400 font-mono pt-1 whitespace-nowrap">{course.slug}</span>
      </div>
      {course.exam && (
        <p className="text-sm text-slate-500 mb-5">{course.exam.display_name}</p>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors border-b-2 -mb-px whitespace-nowrap ${
              tab === t.key
                ? 'border-violet-600 text-violet-700 bg-violet-50'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'info' && <InfoTab course={course} onUpdate={handleUpdate} onUpdateExam={handleUpdateExam} />}
      {tab === 'stats' && <StatsTab stats={course.stats} />}
      {tab === 'pricing' && <PricingTab courseId={courseId} pricing={course.pricing} onRefresh={load} />}
      {tab === 'videos' && <VideosTab groups={course.demo_videos} onSave={handleSaveVideos} />}
      {tab === 'structure' && <StructureTab structure={course.structure} onRefresh={load} />}
      {tab === 'purchases' && <PurchasesTab courseId={courseId} />}
    </div>
  );
}

// ─── Search-based select dropdown ─────────────────────────────────────────────

function SearchSelect({ options, value, onChange, placeholder, clearLabel = 'All', disabled = false }: {
  options: { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
  placeholder: string;
  clearLabel?: string;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()));
  const selected = options.find(o => o.id === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={`flex items-center justify-between gap-2 border border-admin-border rounded-xl px-4 py-2 text-sm bg-white min-w-[180px] transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-violet-400 cursor-pointer'
        }`}
      >
        <span className={selected ? 'text-slate-800' : 'text-slate-400'}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="text-slate-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-20 bg-white border border-admin-border rounded-xl shadow-lg min-w-[220px] overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full text-sm px-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-violet-400"
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            <button
              type="button"
              className="w-full text-left px-4 py-2 text-sm text-slate-400 hover:bg-slate-50"
              onClick={() => { onChange(''); setQuery(''); setOpen(false); }}
            >
              {clearLabel}
            </button>
            {filtered.map(o => (
              <button
                key={o.id}
                type="button"
                className={`w-full text-left px-4 py-2 text-sm hover:bg-violet-50 ${
                  value === o.id ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-slate-700'
                }`}
                onClick={() => { onChange(o.id); setQuery(''); setOpen(false); }}
              >
                {o.label}
              </button>
            ))}
            {filtered.length === 0 && <p className="px-4 py-3 text-sm text-slate-400">No results</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function guessCountryCode(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const map: Record<string, string> = {
      'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
      'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
      'America/Los_Angeles': 'US', 'America/Phoenix': 'US',
      'Europe/London': 'GB',
      'Asia/Dubai': 'AE',
      'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU',
      'America/Toronto': 'CA', 'America/Vancouver': 'CA',
      'Asia/Singapore': 'SG',
      'Pacific/Auckland': 'NZ',
      'Africa/Johannesburg': 'ZA',
      'Europe/Paris': 'EU', 'Europe/Berlin': 'EU', 'Europe/Madrid': 'EU',
      'Asia/Kuala_Lumpur': 'MY',
      'Africa/Lagos': 'NG',
      'Asia/Dhaka': 'BD',
      'Asia/Kathmandu': 'NP',
      'Asia/Colombo': 'LK',
    };
    return map[tz] ?? 'IN';
  } catch { return 'IN'; }
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Courses() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ countries: CountryMeta[]; exams: ExamMeta[] } | null>(null);
  const [countryId, setCountryId] = useState('');
  const [examId, setExamId] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadCourses = useCallback(async (q: string, cid: string, eid: string) => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (q) params.search = q;
    if (eid) params.exam_id = eid;
    else if (cid) params.country_id = cid;
    try { setCourses(await api.listCourses(Object.keys(params).length ? params : undefined)); }
    finally { setLoading(false); }
  }, []);

  // Load meta (countries + exams) once, then auto-select user's country
  useEffect(() => {
    api.listMeta().then(m => {
      setMeta(m);
      const code = guessCountryCode();
      const match = m.countries.find(c => c.code === code);
      if (match) setCountryId(match.id);
    }).catch(() => {});
  }, []);

  // Reload courses when filters change
  useEffect(() => { loadCourses(search, countryId, examId); }, [loadCourses, countryId, examId]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearch(v: string) {
    setSearch(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadCourses(v, countryId, examId), 400);
  }

  function handleCountryChange(cid: string) {
    setCountryId(cid);
    setExamId(''); // clear exam when country changes
  }

  const countryOptions = (meta?.countries ?? []).map(c => ({
    id: c.id,
    label: `${c.flag_emoji} ${c.name}`.trim(),
  }));

  const examOptions = (meta?.exams ?? [])
    .filter(e => !countryId || e.country_id === countryId)
    .map(e => ({ id: e.id, label: e.display_name }));

  const activeFilters = [countryId && 'country', examId && 'exam'].filter(Boolean).length;

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-6">Course Management</h2>

      {selectedId ? (
        <CourseDetailPanel
          courseId={selectedId}
          onBack={() => { setSelectedId(null); loadCourses(search, countryId, examId); }}
        />
      ) : (
        <>
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <input
              value={search}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search courses…"
              className="border border-admin-border rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-violet-400 w-52"
            />

            <SearchSelect
              options={countryOptions}
              value={countryId}
              onChange={handleCountryChange}
              placeholder="All Countries"
              clearLabel="All Countries"
            />

            <SearchSelect
              options={examOptions}
              value={examId}
              onChange={setExamId}
              placeholder={countryId ? 'All Exams' : 'Select country first'}
              clearLabel="All Exams"
              disabled={examOptions.length === 0 && !examId}
            />

            {activeFilters > 0 && (
              <button
                type="button"
                onClick={() => { setCountryId(''); setExamId(''); }}
                className="text-xs text-slate-400 hover:text-red-500 transition-colors underline"
              >
                Clear filters
              </button>
            )}
          </div>

          {loading ? (
            <p className="text-slate-400 text-sm">Loading…</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {courses.map(c => (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  className="bg-white border border-admin-border rounded-xl p-5 text-left hover:border-violet-400 hover:shadow-sm transition-all"
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-bold text-slate-800 text-sm leading-snug">{c.name}</span>
                    <Badge active={c.is_active} />
                  </div>
                  {c.tagline && <p className="text-xs text-slate-500 mb-3 line-clamp-1">{c.tagline}</p>}
                  <div className="border-t border-slate-100 pt-2 mt-2 flex items-center justify-between">
                    <span className="text-xs text-slate-400 font-mono">{c.slug}</span>
                    {(c.enrolled_count ?? 0) > 0 && (
                      <span className="text-xs font-semibold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">
                        👥 {c.enrolled_count}
                      </span>
                    )}
                  </div>
                </button>
              ))}
              {courses.length === 0 && <p className="text-slate-400 text-sm col-span-3">No courses found.</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
